/**
 * Hybrid ContentBlock
 *
 * Routes the whole component between the Tailwind implementation (preferred)
 * and the styled-components implementation (legacy fallback), mirroring
 * ButtonHybrid's single `hasStyledProps` gate.
 *
 * `contentProps` is ContentBlock's only styled-system passthrough surface: it
 * is spread onto the content `<Box>` and may carry props (`bg`, `py`, …) that a
 * plain Tailwind element cannot honor. When it does, render the styled-components
 * tree so those props still apply; otherwise use the lighter Tailwind path.
 */

import React from "react";
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import {
  ContentBlockTailwind,
  ContentFooterTailwind,
  type ContentFooterProps,
} from "./ContentBlockTailwind";
import { ContentBlockStyled } from "./ContentBlockStyled";
import type { ContentBlockProps } from "./ContentBlock";

const ContentBlockHybrid = (props: ContentBlockProps) => {
  if (props.contentProps && hasStyledProps(props.contentProps)) {
    return <ContentBlockStyled {...props} />;
  }

  return <ContentBlockTailwind {...props} />;
};

export default ContentBlockHybrid;

/**
 * Hybrid ContentBlock footer.
 *
 * The footer exposes no styled-system props and no styled()-extendable surface
 * (FooterContainer is internal and never re-exported from index.ts), so the
 * Tailwind/CSS-class footer is always used — no routing predicate is needed here.
 * Kept as a separate export so the footer-override story/tests can render it
 * directly.
 */
export const ContentFooterHybrid = (props: ContentFooterProps) => (
  <ContentFooterTailwind {...props} />
);
